/**
 * Protected Route Component
 *
 * Wraps routes that require authentication.
 * Redirects to login page if user is not authenticated.
 */

import { Navigate, useLocation } from "react-router";
import { useAuth } from "../lib/auth";
import { css } from "styled-system/css";

interface ProtectedRouteProps {
  children: React.ReactNode;
}

export function ProtectedRoute({ children }: ProtectedRouteProps) {
  const { isAuthenticated, isLoading } = useAuth();
  const location = useLocation();

  // Show loading state while checking auth
  if (isLoading) {
    return (
      <div className={styles.loading}>
        <div className={styles.spinner} />
        <p>Loading...</p>
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    // Preserve the intended destination
    const redirectTo = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/login?redirectTo=${redirectTo}`} replace />;
  }

  // User is authenticated, render children
  return <>{children}</>;
}

const styles = {
  loading: css({
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    height: "100vh",
    gap: 4,
    color: "fg.muted",
  }),
  spinner: css({
    width: 10,
    height: 10,
    border: "3px solid",
    borderColor: "border.default",
    borderTopColor: "accent.default",
    borderRadius: "full",
    animation: "spin 1s linear infinite",
  }),
};
